import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { ProgressBar } from "./atoms";
import { useLifeAreas } from "./config";
import { fs, ink, SOFT, T, withAlpha } from "./tokens";
import { RingDimension } from "./types";

// Spec molecule AstriaLifeAreaRow, with the area detail opening underneath.
const AreaRow: React.FC<{
  dim: RingDimension;
  selected: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}> = ({ dim, selected, onToggle, children }) => {
  const L = useLifeAreas();
  const { t } = useTranslation();
  const area = L.area(dim.key);
  const score = L.display(dim.score);
  const band = L.bandFor(score);
  const active = dim.isActive;

  // primary: the state label. secondary: the number (right below it).
  const state = !active
    ? { text: t("lifeAreas.row.off"), color: T.color.text.muted }
    : score == null
      ? { text: t("lifeAreas.detail.notRated"), color: T.color.text.secondary }
      : { text: band?.label ?? "", color: band ? ink(band.color) : T.color.text.secondary };
  const staleNote = active && dim.isStale ? t("lifeAreas.row.stale", { count: dim.staleDays ?? 0 }) : null;

  const Trend = dim.trend === "up" ? ArrowUp : dim.trend === "down" ? ArrowDown : Minus;
  const trendColor =
    dim.trend === "up"
      ? SOFT.up
      : dim.trend === "down"
        ? SOFT.down
        : T.color.text.muted;

  return (
    <li id={`lg2-area-${dim.key}`} style={{ listStyle: "none" }}>
      <button
        type="button"
        className="lg2-row"
        disabled={!active}
        aria-expanded={selected}
        aria-keyshortcuts={String(area.shortcut)}
        title={t("lifeAreas.row.shortcut", { area: area.label, key: area.shortcut })}
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          textAlign: "left",
          padding: "13px 14px",
          background: selected ? withAlpha(area.color, 0.1) : T.color.bg.card,
          border: `1px solid ${selected ? area.color : T.color.bg.overlay}`,
          borderRadius: T.radius.xl,
          boxShadow: selected ? `0 0 12px ${withAlpha(area.color, 0.35)}` : undefined,
          color: T.color.text.primary,
          opacity: active ? 1 : 0.5,
          cursor: active ? "pointer" : "default",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: T.radius.full,
            background: withAlpha(area.color, 0.16),
            fontSize: 18,
          }}
        >
          {area.emoji}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ ...fs("sm", 600), display: "block" }}>{area.label}</span>
          {staleNote && (
            <span style={{ ...fs("xs"), display: "block", color: ink(T.color.accent) }}>{staleNote}</span>
          )}
        </span>
        <span className="hidden min-[440px]:block" style={{ width: 84, flexShrink: 0 }}>
          <ProgressBar value={((score ?? 0) / L.config.scale.max) * 100} color={active ? area.color : T.color.text.muted} label={t("lifeAreas.checkin.scoreLabel", { area: area.label })} />
        </span>
        <span style={{ textAlign: "right", flexShrink: 0, minWidth: 64 }}>
          <span style={{ ...fs("sm", 700), display: "block", color: state.color }}>{state.text}</span>
          {score != null && (
            <span style={{ ...fs("xs"), display: "block", color: T.color.text.secondary }}>{score}</span>
          )}
        </span>
        <Trend size={16} color={trendColor} aria-label={t("lifeAreas.row.trend", { trend: t(`lifeAreas.trend.${dim.trend}`) })} style={{ flexShrink: 0 }} />
      </button>
      {selected && children}
    </li>
  );
};

export default AreaRow;
